/**
 * スタンプの取得と保存(CLAUDE.md §8)
 *
 * 取得の順序が重要:
 *
 *   1. トークン検証・重複チェック(画面側)
 *   2. **先にスタンプを保存する**
 *   3. そのあとで位置情報を待ち、結果を追記する
 *
 * 位置情報の取得は最大12秒かかる。それを待ってから保存すると、参加者が
 * その間にアプリを閉じた・電池が切れた場合にスタンプが消える。
 * 歌碑の前まで歩いた人の記録を失うのが、この機能で最も避けたい失敗。
 * よって「まず記録し、あとから位置を足す」順にしている。
 */

import data from "../data/checkpoints.json";
import {
  findCheckpoint,
  requiredCount,
  type Checkpoint,
  type CheckpointData,
} from "../lib/checkpoints";
import { distanceMeters, estimateMinWalkMinutes, withinGeofence } from "../lib/geo";
import { ulid } from "../lib/crypto";
import { get, getAll, put, STORE } from "./db";
import { locate, type LocateFailure } from "./locate";
import { enqueueStamp } from "./sync-queue";
import { requestSync } from "./sync";

const cps = data as unknown as CheckpointData;

/**
 * 取得経路。
 * "link" は「URLで直接開かれた」= 端末標準のカメラアプリでQRを読み、
 * そこからブラウザが起動した場合。アプリ内カメラで読んだ "qr" とは
 * Web側から区別がつかないため、別の値にしている。
 */
export type StampMethod = "qr" | "word" | "link";

export type StampFlag = "gps_unverified" | "too_fast";

export interface StampGps {
  lat: number;
  lng: number;
  acc: number;
}

export interface Stamp {
  /** keyPath。同一CPは1回のみ(§8-4) */
  cp_id: string;
  /** サーバー側の重複排除キー(§4-4) */
  ulid: string;
  timestamp: number;
  gps: StampGps | null;
  /** CPからの距離(m)。位置が取れたときのみ。 */
  distance_m: number | null;
  method: StampMethod;
  flags: StampFlag[];
  /** 同期キューへ積み終えたか。落ちたときの取りこぼしを起動時に拾う。 */
  queued: boolean;
}

export interface LocateOutcome {
  /** ジオフェンス内にいることを確認できたか */
  verified: boolean;
  /** 位置が取れなかった場合の理由 */
  failure?: LocateFailure;
  /** CPからの距離(m) */
  distanceM: number | null;
}

export interface AcquireOutcome {
  status: "acquired" | "duplicate";
  stamp: Stamp;
  /**
   * 位置確認の結果。acquired のときのみ返る。
   * 解決した時点で保存済みのスタンプも更新され、同期キューに積まれる。
   */
  locating?: Promise<LocateOutcome>;
}

/**
 * 「速すぎる」と判定する比率(§9-3)。
 * CP間の徒歩所要時間の下限に対してこの割合を下回ったらフラグ。
 * 誤検知(正規の参加者を不正扱い)のほうが害が大きいので、
 * 「大きく下回る」を文字どおり半分未満として扱う。
 */
const TOO_FAST_RATIO = 0.5;

export async function getStamps(): Promise<Stamp[]> {
  return getAll<Stamp>(STORE.stamps);
}

export async function getStamp(cpId: string): Promise<Stamp | undefined> {
  return get<Stamp>(STORE.stamps, cpId);
}

export interface Progress {
  collected: number;
  required: number;
  total: number;
  complete: boolean;
}

export async function getStampProgress(): Promise<Progress> {
  const stamps = await getStamps();
  const required = requiredCount(cps);
  return {
    collected: stamps.length,
    required,
    total: cps.checkpoints.length,
    complete: stamps.length >= required,
  };
}

/**
 * スタンプを取得する。重複なら何も書かずに既存を返す。
 */
export async function acquireStamp(
  cp: Checkpoint,
  method: StampMethod,
): Promise<AcquireOutcome> {
  const existing = await getStamp(cp.id);
  if (existing) return { status: "duplicate", stamp: existing };

  const now = Date.now();
  const previous = await getStamps();

  const stamp: Stamp = {
    cp_id: cp.id,
    ulid: ulid(now),
    timestamp: now,
    gps: null,
    distance_m: null,
    method,
    flags: isTooFast(previous, cp, now) ? ["too_fast"] : [],
    queued: false,
  };

  await put(STORE.stamps, stamp);

  return { status: "acquired", stamp, locating: attachLocation(stamp, cp) };
}

/** 位置を確認してスタンプに追記し、同期キューへ積む */
async function attachLocation(stamp: Stamp, cp: Checkpoint): Promise<LocateOutcome> {
  const result = await locate();
  let outcome: LocateOutcome;

  if (result.ok) {
    const inside = withinGeofence(result, cp, cp.geofence_m);
    stamp.gps = { lat: result.lat, lng: result.lng, acc: result.acc };
    stamp.distance_m = Math.round(distanceMeters(result, cp));
    if (!inside) stamp.flags.push("gps_unverified");
    outcome = { verified: inside, distanceM: stamp.distance_m };
  } else {
    stamp.flags.push("gps_unverified");
    outcome = { verified: false, failure: result.reason, distanceM: null };
  }

  await put(STORE.stamps, stamp);
  await markQueued(stamp);
  return outcome;
}

async function markQueued(stamp: Stamp): Promise<void> {
  await enqueueStamp(stamp);
  stamp.queued = true;
  await put(STORE.stamps, stamp);
  // 電波があればこの場で送る。無ければ黙ってキューに残る(§4-2)。
  requestSync();
}

/**
 * 起動時の取りこぼし回収。
 *
 * 位置情報を待っている最中にアプリが落ちると、スタンプは保存済みだが
 * 同期キューに積まれていない状態が残る。位置は後から取り直せないので、
 * gps_unverified として確定させたうえでキューに積む。
 */
export async function reconcileStamps(): Promise<number> {
  const stamps = await getStamps();
  let fixed = 0;

  for (const stamp of stamps) {
    if (stamp.queued) continue;
    if (!stamp.gps && !stamp.flags.includes("gps_unverified")) {
      stamp.flags.push("gps_unverified");
    }
    await markQueued(stamp);
    fixed++;
  }
  return fixed;
}

/**
 * 直前までに取ったどのスタンプから見ても短すぎる間隔で来ていないかを見る(§9-3)。
 *
 * 直近1件だけでなく全件と比べているのは、離れたCPから瞬間移動してきた場合を
 * 拾うため。min_walk_minutes が未設定のペアは座標から推定する。
 */
function isTooFast(previous: Stamp[], cp: Checkpoint, now: number): boolean {
  for (const s of previous) {
    const other = findCheckpoint(cps, s.cp_id);
    if (!other) continue;

    const declared = cp.min_walk_minutes?.[other.id] ?? other.min_walk_minutes?.[cp.id];
    const needMinutes = declared ?? estimateMinWalkMinutes(cp, other);
    const elapsedMinutes = (now - s.timestamp) / 60_000;

    if (elapsedMinutes < needMinutes * TOO_FAST_RATIO) return true;
  }
  return false;
}
